const STORAGE_KEY = "momo_impersonation";
export const IMPERSONATION_EVENT = "momo-impersonation-change";

function notifyImpersonationChange(payload = null) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(IMPERSONATION_EVENT, { detail: payload }));
}

function readStoredImpersonation() {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.token || !parsed.userId) return null;
    if (parsed.expiresAt) {
      const expiresAtMs = Date.parse(parsed.expiresAt);
      if (!Number.isNaN(expiresAtMs) && Date.now() >= expiresAtMs) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
    }
    return parsed;
  } catch (e) {
    return null;
  }
}

export function getImpersonation() {
  return readStoredImpersonation();
}

export function setImpersonation({ token, userId, userInfoId, expiresAt }) {
  const payload = {
    token,
    userId,
    userInfoId,
    username: undefined,
    email: undefined,
    expiresAt: expiresAt || null,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  notifyImpersonationChange(payload);
  return payload;
}

export function setImpersonationIdentity({ username, email }) {
  const current = getImpersonation();
  if (!current) return null;
  const updated = {
    ...current,
    username: username ?? current.username,
    email: email ?? current.email,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  notifyImpersonationChange(updated);
  return updated;
}

export function clearImpersonation() {
  localStorage.removeItem(STORAGE_KEY);
  notifyImpersonationChange(null);
}

export function isImpersonating() {
  return !!getImpersonation();
}

export function getEffectiveUserId(sessionUserId) {
  const imp = getImpersonation();
  if (imp?.userId) return imp.userId;
  return sessionUserId || null;
}

export function getEffectiveAuthToken(session) {
  const imp = getImpersonation();
  if (imp?.token) return imp.token;
  return session?.access_token || null;
}

export function buildAuthHeaders(session, { includeContentType = true } = {}) {
  const token = getEffectiveAuthToken(session);
  const headers = {};
  if (includeContentType) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/**
 * POST /momo/users/:userInfoId/impersonate (session token), persist impersonation state.
 * @throws {Error} with server detail or fallback message
 */
export async function startImpersonation({
  session,
  userInfoId,
  username = "",
  email = "",
  apiUrl,
  impersonationFailedMessage = "No se pudo iniciar suplantación de usuario",
}) {
  if (!session?.access_token) {
    throw new Error(impersonationFailedMessage);
  }
  const id = Number(userInfoId);
  if (!Number.isInteger(id) || id <= 0) {
    throw new Error(impersonationFailedMessage);
  }
  const base = apiUrl || "http://localhost:8000";
  const response = await fetch(`${base}/momo/users/${id}/impersonate`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    const detail = errData?.detail;
    throw new Error(
      typeof detail === "string" && detail
        ? detail
        : response.statusText || impersonationFailedMessage
    );
  }

  const data = await response.json();
  setImpersonation({
    token: data.token,
    userId: data.user_id,
    userInfoId: data.user_info_id,
    expiresAt: data.expires_at,
  });
  setImpersonationIdentity({
    username: username || "",
    email: email || "",
  });
}
